import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { BORDER, DOMAIN_HEX, ELEVATION, INK, STATUS_HEX, SURFACE } from '../design-system/foundation';

/**
 * KALEIDOSCOPE — legacy token names kept for existing imports.
 *
 * Neutrals (surfaces, text, borders) are CSS variables so they follow
 * light/dark mode; they used to be dark-only hex, which made text vanish in
 * light mode. Pass them through `safeAlpha` (design-system/tokens), never MUI
 * `alpha()` — alpha() throws on var(). Accent/status values stay literal hex.
 */
export type DomainId = 'discover' | 'understand' | 'change' | 'operate';

/**
 * Solid dark hex surfaces — only for always-dark stages (graph canvases,
 * diagram panels). Do not use for page chrome.
 */
export const SURFACE_LAYERS = {
  app: '#0b0e16',
  workspace: '#141824',
  section: '#1a2030',
  interactive: '#202840',
  focused: '#262f4c',
  selected: '#2c3658',
  overlay: 'rgba(11, 14, 22, 0.92)',
  aiTint: 'rgba(139, 158, 255, 0.12)',
  criticalTint: 'rgba(242, 85, 106, 0.1)',
} as const;

export const KALEIDOSCOPE = {
  canvas: SURFACE.app,
  canvasAlt: SURFACE.panel,

  surface: SURFACE.panel,
  surfaceSolid: SURFACE.panel,
  surfaceElevated: SURFACE.card,
  surfaceElevatedSolid: SURFACE.card,
  surfaceHover: SURFACE.hover,
  surfaceSelected: SURFACE.selected,

  ink: INK.primary,
  muted: INK.secondary,
  tertiary: INK.tertiary,
  ghost: INK.ghost,

  border: BORDER.subtle,
  borderLight: BORDER.default,
  borderStrong: BORDER.strong,

  chrome: STATUS_HEX.active,
  chromeHover: '#A5B4FF',
  chromeMuted: 'rgba(139, 158, 255, 0.18)',

  discover: DOMAIN_HEX.discover,
  understand: DOMAIN_HEX.understand,
  change: DOMAIN_HEX.change,
  operate: DOMAIN_HEX.operate,
  sky: STATUS_HEX.info,

  success: STATUS_HEX.success,
  warning: STATUS_HEX.warning,
  error: STATUS_HEX.error,

  /** Always-dark diagram stage palette (both color modes). */
  diagram: {
    canvas: '#151A24',
    grid: 'rgba(255, 255, 255, 0.055)',
    wire: '#7EB3E8',
    wireCritical: '#FBBF24',
    nodeDefault: '#2A3F5C',
    nodeStart: '#E89540',
    nodeFlow: '#356194',
    panelBg: SURFACE_LAYERS.section,
  },

  elevation: ELEVATION,
} as const;

export const DOMAIN_COLORS: Record<DomainId, string> = DOMAIN_HEX;

export const KALEIDOSCOPE_CHART = [
  STATUS_HEX.success,
  '#60A5FA',
  '#A78BFA',
  STATUS_HEX.warning,
  '#F472B6',
  '#38BDF8',
] as const;

/** Panel recipes — same card language as design-system/v2 surfaceSx */
export const PANEL_CSS = {
  base: {
    backgroundColor: SURFACE.panel,
    border: `1px solid ${BORDER.default}`,
    boxShadow: 'none',
  },
  elevated: {
    backgroundColor: SURFACE.panel,
    border: `1px solid ${BORDER.default}`,
    boxShadow: ELEVATION.sm,
  },
  hero: {
    backgroundColor: SURFACE.panel,
    border: `1px solid ${BORDER.default}`,
    boxShadow: ELEVATION.md,
  },
  bordered: {
    backgroundColor: SURFACE.panel,
    border: `1px solid ${BORDER.default}`,
    boxShadow: 'none',
  },
} as const;

/** @deprecated use panelSx — kept for imports */
export const GLASS_PANEL_CSS = PANEL_CSS;

export function panelSx(level: 'base' | 'elevated' | 'hero' | 'bordered' = 'base'): SxProps<Theme> {
  return PANEL_CSS[level];
}

/** @deprecated use panelSx */
export const glassPanelSx = panelSx;

export function domainGlowSx(color: string, intensity = 0.2): SxProps<Theme> {
  return {
    boxShadow: `0 0 12px ${alpha(color, intensity)}`,
  };
}

export function domainCardSx(color: string): SxProps<Theme> {
  return {
    height: '100%',
    ...PANEL_CSS.elevated,
    bgcolor: `color-mix(in srgb, ${color} 4%, var(--panel-bg))`,
    transition: 'background-color 0.2s ease',
    '&:hover': {
      bgcolor: `color-mix(in srgb, ${color} 8%, var(--panel-bg))`,
    },
  };
}

export function domainProgressSx(color: string): SxProps<Theme> {
  return {
    height: 8,
    borderRadius: 2,
    bgcolor: alpha(color, 0.12),
    '& .MuiLinearProgress-bar': {
      borderRadius: 2,
      bgcolor: color,
    },
  };
}
